import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Box, Grid,
  TextField, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Alert, CircularProgress,
  MenuItem, Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import AddIcon        from '@mui/icons-material/Add';
import EditIcon       from '@mui/icons-material/Edit';
import DeleteIcon     from '@mui/icons-material/Delete';
import StarIcon       from '@mui/icons-material/Star';
import HomeIcon       from '@mui/icons-material/Home';
import WorkIcon       from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon  from '@mui/icons-material/ArrowBack';
import GpsFixedIcon   from '@mui/icons-material/GpsFixed';
import userService    from '../../services/user.service';

/* ── Design tokens ── */
const T = {
  purple: '#552583',
  purpleDark: '#3a1760',
  gold: '#FDB927',
  bg: '#E4F0F6',
  card: '#ffffff',
  text: '#1a1a1a',
  soft: '#4a4a4a',
  muted: 'rgba(0,0,0,0.4)',
  green: '#22c55e',
  red: '#ef4444',
  blue: '#3b82f6',
  orange: '#f59e0b',
  border: 'rgba(0,0,0,0.06)',
  radius: { input: '10px', btn: '12px', card: '20px', dialog: '20px' },
};

const labelOptions = [
  { value: 'Maison', icon: <HomeIcon />,      color: T.purple, bg: 'rgba(85,37,131,0.07)' },
  { value: 'Bureau', icon: <WorkIcon />,       color: T.blue,   bg: 'rgba(59,130,246,0.07)' },
  { value: 'Autre',  icon: <LocationOnIcon />, color: T.orange, bg: 'rgba(245,158,11,0.07)' },
];

const cities = [
  'Mamoudzou', 'Koungou', 'Dzaoudzi', 'Dembeni', 'Tsingoni',
  'Bandrele', 'Ouangani', 'Chiconi', 'Sada', 'Boueni',
  'Chirongui', 'Kani-Keli', 'Mtsamboro', 'Acoua', 'Bandraboua', 'Pamandzi',
];

const emptyAddress = {
  id: null, label: 'Maison', street_number: '', address_line1: '', address_line2: '',
  city: '', postal_code: '', latitude: null, longitude: null, is_default: false,
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: T.radius.input,
    '& fieldset': { borderColor: 'rgba(85,37,131,0.14)' },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(85,37,131,0.45)', borderWidth: '1.5px' },
  },
};

// ──────────────────────────────────────────────────────────────────
const Addresses = () => {
  const [addresses,      setAddresses]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [dialogOpen,     setDialogOpen]     = useState(false);
  const [editMode,       setEditMode]       = useState(false);
  const [currentAddress, setCurrentAddress] = useState({ ...emptyAddress });
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');
  const [formErrors,     setFormErrors]     = useState({});
  const [saving,         setSaving]         = useState(false);

  useEffect(() => { loadAddresses(); }, []);

  const loadAddresses = async () => {
    try {
      const data = await userService.getAddresses();
      setAddresses(data.addresses || []);
    } catch { setError('Erreur lors du chargement des adresses'); }
    finally   { setLoading(false); }
  };

  const validate = () => {
    const errors = {};
    if (!currentAddress.address_line1.trim()) errors.address_line1 = "L'adresse est requise";
    if (!currentAddress.city.trim())          errors.city           = 'La ville est requise';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true); setError('');
    try {
      const { street_number, ...rest } = currentAddress;
      const payload = {
        ...rest,
        address_line1: [street_number, rest.address_line1].filter(Boolean).join(' '),
      };
      if (editMode) {
        await userService.updateAddress(currentAddress.id, payload);
        setSuccess('Adresse modifiée');
      } else {
        await userService.addAddress(payload);
        setSuccess('Adresse ajoutée');
      }
      setDialogOpen(false);
      loadAddresses();
      resetForm();
      setTimeout(() => setSuccess(''), 3500);
    } catch { setError('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette adresse ?')) return;
    try {
      await userService.deleteAddress(id);
      setSuccess('Adresse supprimée');
      loadAddresses();
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Erreur lors de la suppression'); }
  };

  const handleSetDefault = async (id) => {
    try {
      await userService.setDefaultAddress(id);
      loadAddresses();
    } catch { setError('Erreur lors de la mise à jour'); }
  };

  const openDialog = (address = null) => {
    setFormErrors({}); setError('');
    if (address) { setCurrentAddress({ ...address }); setEditMode(true); }
    else         { resetForm(); setEditMode(false); }
    setDialogOpen(true);
  };

  const resetForm = () => { setCurrentAddress({ ...emptyAddress }); setFormErrors({}); };
  const updateField = (field, value) => {
    setCurrentAddress(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const getOption = (label) => labelOptions.find(o => o.value === label) || labelOptions[2];

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" sx={{ bgcolor: T.bg }}>
      <CircularProgress sx={{ color: T.purple }} size={28} thickness={2.5} />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: '100vh', pb: 8 }}>

      {/* ── Header ── */}
      <Box sx={{ pt: 4, pb: 5 }}>
        <Container maxWidth="lg">
          <Button component={Link} to="/profile" startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ mb: 3, color: T.muted, textTransform: 'none', fontWeight: 600, fontSize: '0.84rem',
              px: 0, minWidth: 0,
              '&:hover': { color: T.purple, background: 'transparent' } }}>
            Retour au profil
          </Button>
          <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2rem' }, color: T.text, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 0.5 }}>
                Mes adresses
              </Typography>
              <Typography sx={{ color: T.muted, fontSize: '0.88rem' }}>
                Gérez vos adresses de livraison
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
              sx={{
                bgcolor: T.purple, color: 'white', fontWeight: 700,
                borderRadius: T.radius.btn, px: 3, py: 1.3, textTransform: 'none', fontSize: '0.88rem',
                boxShadow: '0 4px 16px rgba(85,37,131,0.25)', flexShrink: 0,
                '&:hover': { bgcolor: T.purpleDark, boxShadow: '0 6px 24px rgba(85,37,131,0.35)' },
              }}>
              Ajouter une adresse
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: T.radius.input }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: T.radius.input }} onClose={() => setSuccess('')}>{success}</Alert>}

        {addresses.length === 0 ? (
          /* ── Empty state ── */
          <Box sx={{
            background: T.card, borderRadius: T.radius.card,
            border: `1px solid ${T.border}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            textAlign: 'center', py: 8, px: 3,
          }}>
            <LocationOnIcon sx={{ fontSize: 40, color: 'rgba(85,37,131,0.2)', mb: 2 }} />
            <Typography sx={{ fontWeight: 800, color: T.text, mb: 0.5, fontSize: '1.1rem' }}>Aucune adresse enregistrée</Typography>
            <Typography sx={{ color: T.muted, mb: 3, fontSize: '0.88rem' }}>Ajoutez votre première adresse de livraison</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
              sx={{
                background: T.purple, borderRadius: T.radius.btn, textTransform: 'none',
                fontWeight: 700, px: 3.5, py: 1.2, fontSize: '0.88rem',
                boxShadow: '0 4px 16px rgba(85,37,131,0.25)',
                '&:hover': { background: T.purpleDark },
              }}>
              Ajouter une adresse
            </Button>
          </Box>
        ) : (
          /* ── Address cards ── */
          <Grid container spacing={2}>
            {addresses.map(addr => {
              const opt = getOption(addr.label);
              return (
                <Grid item xs={12} sm={6} md={4} key={addr.id}>
                  <Box sx={{
                    background: T.card, borderRadius: T.radius.card,
                    border: `1px solid ${T.border}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    p: 2.5, height: '100%',
                    display: 'flex', flexDirection: 'column',
                    transition: 'box-shadow 0.15s ease',
                    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
                  }}>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{
                          width: 38, height: 38, borderRadius: '10px',
                          bgcolor: opt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: opt.color,
                        }}>
                          {React.cloneElement(opt.icon, { sx: { fontSize: 20 } })}
                        </Box>
                        <Typography sx={{ fontWeight: 800, color: T.text, fontSize: '1rem' }}>{addr.label}</Typography>
                      </Box>
                      {addr.is_default && (
                        <Chip
                          icon={<StarIcon sx={{ fontSize: '12px !important', color: `${T.gold} !important` }} />}
                          label="Défaut"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(253,185,39,0.1)', color: T.gold,
                            border: '1px solid rgba(253,185,39,0.25)',
                            fontWeight: 700, fontSize: '0.65rem', height: 22,
                          }}
                        />
                      )}
                    </Box>

                    {/* Address */}
                    <Box sx={{ mb: 1.5, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: T.text, fontSize: '0.88rem', mb: 0.3 }}>{[addr.street_number, addr.address_line1].filter(Boolean).join(' ')}</Typography>
                      {addr.address_line2 && <Typography sx={{ color: T.muted, fontSize: '0.8rem', mb: 0.3 }}>{addr.address_line2}</Typography>}
                      <Typography sx={{ color: T.soft, fontSize: '0.84rem' }}>{addr.city}{addr.postal_code ? ` · ${addr.postal_code}` : ''}</Typography>
                    </Box>

                    {/* GPS */}
                    {addr.latitude && (
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
                        <GpsFixedIcon sx={{ fontSize: 11, color: T.green }} />
                        <Typography sx={{ color: T.green, fontWeight: 600, fontSize: '0.7rem' }}>
                          GPS ({Number(addr.latitude).toFixed(3)}, {Number(addr.longitude).toFixed(3)})
                        </Typography>
                      </Box>
                    )}

                    {/* Actions */}
                    <Box display="flex" gap={0.8} alignItems="center" pt={1.5} sx={{ borderTop: `1px solid ${T.border}` }}>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => openDialog(addr)}
                          sx={{ color: T.blue, borderRadius: '8px', width: 30, height: 30,
                            '&:hover': { bgcolor: 'rgba(59,130,246,0.08)' } }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" onClick={() => handleDelete(addr.id)}
                          sx={{ color: T.red, borderRadius: '8px', width: 30, height: 30,
                            '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      {!addr.is_default && (
                        <Button size="small" onClick={() => handleSetDefault(addr.id)}
                          startIcon={<StarIcon sx={{ fontSize: '12px !important' }} />}
                          sx={{
                            ml: 'auto', color: T.muted, textTransform: 'none',
                            fontSize: '0.73rem', fontWeight: 600, borderRadius: '8px', px: 1,
                            '&:hover': { color: T.gold, bgcolor: 'rgba(253,185,39,0.06)' },
                          }}>
                          Défaut
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* ── Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: T.radius.dialog, overflow: 'hidden', bgcolor: T.card } }}>

        <DialogTitle sx={{ pb: 0.5, pt: 3, px: 3 }}>
          <Typography sx={{ fontWeight: 800, color: T.text, fontSize: '1.2rem', letterSpacing: '-0.02em' }}>
            {editMode ? "Modifier l'adresse" : 'Nouvelle adresse'}
          </Typography>
          <Typography sx={{ color: T.muted, fontSize: '0.82rem', mt: 0.3 }}>
            {editMode ? 'Modifiez les informations' : 'Ajoutez une adresse de livraison'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, px: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: T.radius.input }} onClose={() => setError('')}>{error}</Alert>}

          <Grid container spacing={2}>

            {/* Type */}
            <Grid item xs={12}>
              <Typography sx={{ color: T.muted, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.68rem', mb: 1 }}>
                Type d'adresse
              </Typography>
              <Box display="flex" gap={1}>
                {labelOptions.map(opt => {
                  const selected = currentAddress.label === opt.value;
                  return (
                    <Box key={opt.value} onClick={() => updateField('label', opt.value)} sx={{
                      flex: 1, textAlign: 'center', py: 1.5, px: 1, borderRadius: '12px', cursor: 'pointer',
                      border: `1.5px solid ${selected ? opt.color : 'rgba(0,0,0,0.08)'}`,
                      bgcolor: selected ? opt.bg : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: opt.color },
                    }}>
                      <Box sx={{ color: opt.color, mb: 0.3 }}>
                        {React.cloneElement(opt.icon, { sx: { fontSize: 22 } })}
                      </Box>
                      <Typography sx={{
                        fontSize: '0.75rem', fontWeight: selected ? 700 : 500,
                        color: selected ? opt.color : T.muted,
                      }}>
                        {opt.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            {/* N° + Rue */}
            <Grid item xs={4} sm={3}>
              <TextField fullWidth label="N°" value={currentAddress.street_number || ''}
                onChange={e => updateField('street_number', e.target.value)}
                placeholder="12" sx={inputSx} />
            </Grid>
            <Grid item xs={8} sm={9}>
              <TextField fullWidth label="Rue *" value={currentAddress.address_line1}
                onChange={e => { updateField('address_line1', e.target.value); }}
                placeholder="Rue des Baobabs"
                error={!!formErrors.address_line1} helperText={formErrors.address_line1}
                sx={inputSx} />
            </Grid>

            {/* Complément */}
            <Grid item xs={12}>
              <TextField fullWidth label="Complément (optionnel)" value={currentAddress.address_line2 || ''}
                onChange={e => updateField('address_line2', e.target.value)}
                placeholder="Appartement, étage, bâtiment..."
                sx={inputSx} />
            </Grid>

            {/* Ville + CP */}
            <Grid item xs={12} sm={8}>
              <TextField fullWidth select label="Ville *" value={currentAddress.city}
                onChange={e => updateField('city', e.target.value)}
                error={!!formErrors.city} helperText={formErrors.city}
                sx={inputSx}>
                <MenuItem value="" disabled>Sélectionnez une ville</MenuItem>
                {cities.map(city => <MenuItem key={city} value={city}>{city}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Code postal" value={currentAddress.postal_code || ''}
                onChange={e => updateField('postal_code', e.target.value)}
                placeholder="97600"
                sx={inputSx} />
            </Grid>

            {/* Default switch */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: '12px', bgcolor: 'rgba(85,37,131,0.03)', border: '1px solid rgba(85,37,131,0.08)' }}>
                <FormControlLabel
                  control={
                    <Switch checked={currentAddress.is_default || false} onChange={e => updateField('is_default', e.target.checked)}
                      size="small"
                      sx={{ '& .Mui-checked': { color: T.purple }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${T.purple} !important` } }} />
                  }
                  label={
                    <Box ml={0.5}>
                      <Typography sx={{ fontWeight: 700, color: T.text, fontSize: '0.88rem', lineHeight: 1.2 }}>Adresse par défaut</Typography>
                      <Typography sx={{ color: T.muted, fontSize: '0.75rem' }}>Présélectionnée lors de vos commandes</Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: `1px solid ${T.border}` }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}
            sx={{ color: T.muted, textTransform: 'none', borderRadius: T.radius.btn, fontWeight: 600, px: 2, fontSize: '0.88rem' }}>
            Annuler
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{
              background: T.purple, color: 'white',
              textTransform: 'none', borderRadius: T.radius.btn, fontWeight: 700, px: 3.5, py: 1.2,
              fontSize: '0.88rem',
              boxShadow: '0 4px 16px rgba(85,37,131,0.25)',
              '&:hover': { background: T.purpleDark },
              '&:disabled': { background: 'rgba(85,37,131,0.12)', color: 'rgba(0,0,0,0.25)' },
            }}>
            {saving
              ? <CircularProgress size={20} sx={{ color: 'white' }} />
              : editMode ? 'Enregistrer' : "Ajouter l'adresse"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Addresses;
